import { useLabel } from "@/core/context/LabelsContext"
import FormStore from "@/core/form/store/FormStore"
import { ExperienceSettingsAccessFormFieldsFragment$key } from "@/product/settings/__generated__/ExperienceSettingsAccessFormFieldsFragment.graphql"
import { ExperienceSettingsFormMutation } from "@/product/settings/__generated__/ExperienceSettingsFormMutation.graphql"
import ExperienceSettingsAccessSelect from "@/product/settings/ExperienceSettingsAccessSelect"
import ExperienceSettingsAutojoinFormField from "@/product/settings/ExperienceSettingsAutojoinFormField"
import ExperienceSettingsCapacityField from "@/product/settings/ExperienceSettingsCapacityField"
import { ExperienceSettingsFormState } from "@/product/settings/ExperienceSettingsForm"
import ExperienceSettingsLearnModeFields from "@/product/settings/ExperienceSettingsLearnModeFields"
import ExperienceSettingsRegistrationFormFields from "@/product/settings/ExperienceSettingsRegistrationFormFields"
import { useExperienceSettingsSelectedPlans_membershipPlansFragment$key } from "@/product/settings/hooks/__generated__/useExperienceSettingsSelectedPlans_membershipPlansFragment.graphql"
import ProductSettingsMembershipPlans from "@/product/settings/ProductSettingsMembershipPlans"
import Relay from "@/relay/relayUtils"
import { DiscoDivider } from "@disco-ui"
import DiscoFormCollapsibleSection, {
  DiscoFormCollapsibleSectionSkeleton,
} from "@disco-ui/form-collapsible-section/DiscoFormCollapsibleSection"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import { observer } from "mobx-react-lite"
import { graphql, useFragment } from "react-relay"
import classes from "./ExperienceSettingsAccessFormFields.module.css"

interface Props {
  form: FormStore<ExperienceSettingsFormState, ExperienceSettingsFormMutation>
  organizationKey: useExperienceSettingsSelectedPlans_membershipPlansFragment$key
  productKey: ExperienceSettingsAccessFormFieldsFragment$key
}

function ExperienceSettingsAccessFormFields({
  form,
  organizationKey,
  productKey,
}: Props) {
  const { perPlanPricing } = useFeatureFlags()
  const experienceLabel = useLabel("experience")

  const showAutoJoin =
    form.state.registrationAvailability !== "hidden" || form.state.isAutoJoined

  const product = useFragment<ExperienceSettingsAccessFormFieldsFragment$key>(
    graphql`
      fragment ExperienceSettingsAccessFormFieldsFragment on Product {
        ...ExperienceSettingsAccessSelect_ProductFragment
      }
    `,
    productKey
  )

  return (
    <>
      <DiscoFormCollapsibleSection
        sectionId={"visibility"}
        title={"Visibility"}
        description={`Who can view and enroll in the ${experienceLabel.singular}`}
        icon={"lock-unlocked"}
        openByDefault
      >
        <DiscoFormCollapsibleSection.Group>
          <ExperienceSettingsAccessSelect
            sublabel={"Select one"}
            form={form}
            productKey={product}
          />
        </DiscoFormCollapsibleSection.Group>
      </DiscoFormCollapsibleSection>
      <DiscoDivider marginTop={2.5} marginBottom={2.5} />

      <DiscoFormCollapsibleSection
        sectionId={"enrollment"}
        title={"Enrollment"}
        description={`Enrollment requirements for this ${experienceLabel.singular}`}
        icon={"bullseye"}
      >
        <ProductSettingsMembershipPlans
          form={form}
          organizationKey={organizationKey}
          hideToggle={!perPlanPricing}
          hideUnselectedPlans={!perPlanPricing}
        />
        {showAutoJoin && (
          <DiscoFormCollapsibleSection.Group>
            <ExperienceSettingsAutojoinFormField
              form={form}
              organizationKey={organizationKey}
            />
          </DiscoFormCollapsibleSection.Group>
        )}
        {!form.state.isAutoJoined && (
          <>
            <ExperienceSettingsRegistrationFormFields form={form} />
            <DiscoFormCollapsibleSection.Group>
              <ExperienceSettingsCapacityField form={form} />
            </DiscoFormCollapsibleSection.Group>
          </>
        )}
        <DiscoFormCollapsibleSection.Group>
          <ExperienceSettingsLearnModeFields form={form} />
        </DiscoFormCollapsibleSection.Group>
      </DiscoFormCollapsibleSection>
    </>
  )
}

export const ExperienceSettingsAccessFormFieldsSkeleton = () => {
  return (
    <div className={classes.skeletonContainer}>
      <DiscoFormCollapsibleSectionSkeleton />
      <DiscoDivider marginBottom={2.5} marginTop={2.5} />
      <DiscoFormCollapsibleSectionSkeleton />
      <DiscoDivider marginBottom={2.5} marginTop={2.5} />
      <DiscoFormCollapsibleSectionSkeleton />
    </div>
  )
}

export default Relay.withSkeleton<Props>({
  component: observer(ExperienceSettingsAccessFormFields),
  skeleton: ExperienceSettingsAccessFormFieldsSkeleton,
})
